import { MutableRefObject, ReactElement, useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { Alert, Grid, Stack } from '@mui/material';
import { AwsClientOverrides, getSchema } from '@localstack/services';
import { DEFAULT_S3_ROUTES } from '@localstack/constants';
import { CreateWebFunctionRevisionRequest, LambdaWebRevisionFormValues } from '@localstack/types';

import { ControlledDropzone, ControlledRadioGroup, MagicForm, RelatedResourcePicker } from '@localstack/ui';

import { FormFieldset } from '../styled';

type RevisionUploadOptions = Pick<LambdaWebRevisionFormValues, 'codeSource' | 'zipFile'>;
interface UploadOptionsProps {
  optionsReader: MutableRefObject<() => RevisionUploadOptions>;
}
const UploadOptions = ({ optionsReader }: UploadOptionsProps): ReactElement => {
  const { control, getValues } = useFormContext<{ uploadOptions: RevisionUploadOptions }>();
  const codeSource = useWatch({ control, name: 'uploadOptions.codeSource' });
  useEffect(() => {
    optionsReader.current = () => getValues('uploadOptions');
  }, [getValues, optionsReader]);
  return (
    <Stack spacing={2}>
      <ControlledRadioGroup
        control={control}
        name="uploadOptions.codeSource"
        label="Code Source"
        aria-label="Code source"
        row
        options={[
          { value: 'upload', label: 'Upload ZIP file' },
          { value: 's3', label: 'Amazon S3' },
        ]}
      />
      {codeSource === 'upload' && (
        <Alert severity="info">
          Uploads use a new object in the S3 bucket and folder of the latest revision. The execution role must have read
          access to the new object. Existing objects, bucket settings and IAM permissions are not changed. For a role
          restricted to a specific key, upload to an allowed S3 location and choose Amazon S3 instead.
        </Alert>
      )}
      {codeSource === 'upload' && (
        <ControlledDropzone
          control={control}
          name="uploadOptions.zipFile"
          accept=".zip,application/zip"
          shouldUnregister
          rules={{ required: 'Select a ZIP file' }}
        />
      )}
    </Stack>
  );
};

export interface LambdaWebRevisionCreateFormProps {
  formId: string;
  loading?: boolean;
  clientOverrides?: AwsClientOverrides;
  onSubmit: (data: Partial<CreateWebFunctionRevisionRequest>, options: RevisionUploadOptions) => unknown;
}
export const LambdaWebRevisionCreateForm = ({
  formId,
  loading,
  clientOverrides,
  onSubmit,
}: LambdaWebRevisionCreateFormProps): ReactElement => {
  const optionsReader = useRef<() => RevisionUploadOptions>(() => ({ codeSource: 'upload' }));
  return (
    <FormFieldset disabled={loading}>
      <MagicForm
        formId={formId}
        loading={loading}
        schema={getSchema('LambdaWeb')}
        entry="CreateWebFunctionRevisionRequest"
        onSubmit={(data: Partial<CreateWebFunctionRevisionRequest>) => onSubmit(data, optionsReader.current())}
        defaultValues={{ uploadOptions: { codeSource: 'upload' } }}
        fieldConditions={{
          '^functionName$': false,
          '^kmsKeyArn$': false,
          '^serviceConfig$': false,
          '^buildConfig$': ['$uploadOptions.codeSource', '===', 's3'],
          '^buildConfig\\.runtimeConfig$': false,
        }}
        injectedFields={{
          '^description$': {
            position: 'before',
            element: (
              <Grid item xs={12}>
                <UploadOptions optionsReader={optionsReader} />
              </Grid>
            ),
          },
        }}
        externalFields={{
          '^buildConfig\\.codeConfig\\.s3Object\\.bucket$': (control, fieldName, required) => (
            <RelatedResourcePicker
              control={control}
              fieldName={fieldName}
              client="S3"
              method="listBuckets"
              arrayKeyName="Buckets"
              property="Name"
              entityName="S3 Bucket"
              creationRoute={DEFAULT_S3_ROUTES.RESOURCES_S3_BUCKET_CREATE}
              clientOverrides={clientOverrides}
              required={required}
            />
          ),
        }}
      />
    </FormFieldset>
  );
};
